import React from 'react'

import pick from 'lodash/pick'
import upperFirst from 'lodash/upperFirst'

import { NoteType, NoteTypeWithStatus, getEmptyNote } from 'shared/components/Notes/models/note'
import { ConfirmationType, getEmptyConfirmation } from 'shared/components/Notes/models/confirmation'
import { CommentTypeWithStatus, CommentType, getEmptyComment } from 'shared/components/Notes/models/comment'

import CreateNoteDialog from 'shared/components/Notes/components/Dialogs/CreateNoteDialog'
import BasicDialog from 'shared/components/Notes/components/Dialogs/BasicDialog'
import UnflagDialog from 'shared/components/Notes/components/Dialogs/UnflagDialog'
import PreviewNoteDialog from 'shared/components/Notes/components/Dialogs/PreviewNoteDialog'
import PreviewAttachment from 'shared/components/Notes/components/Dialogs/PreviewAttachment'
import ConfirmationDialog from 'shared/components/Notes/components/Dialogs/ConfirmationDialog'
import ActionConfirmationDialog from 'shared/components/Notes/components/Dialogs/ActionConfirmationDialog'
import FlagActionConfirmationDialog from 'shared/components/Notes/components/Dialogs/FlagActionConfirmationDialog'
import useMutation, { useCreateNode, useDeleteNode, useUpdateNode } from 'shared/hooks/useMutation'

import { lighten } from 'polished'
import { useDispatch, useSelector } from 'react-redux'
import { authSelector } from 'shared/hocs/withAuth'
import { showDialog, hideDialog } from 'shared/redux/app/reducer'

import { ParticipantRowItem } from 'pages/Participants/model/participant'

export type NoteActions = {
  refetch?: () => void
  hasAdminPermission?: boolean
  user_id: string
}

export type MemberFlagParams = {
  onValid?: (data?: ConfirmationType) => void
  onSuccess?: (data?: any) => void
  user_id?: string
}

export type MemberUnFlagParams = {
  onValid?: (data?: ConfirmationType) => void
  onSuccess?: (data?: any) => void
  user_id?: string
  type?: string
}

export type Preview = {
  onValid?: (data?: NoteType) => void
  onSuccess?: (data?: NoteTypeWithStatus) => void
  user_id?: string
  note_id?: string
  participant?: ParticipantRowItem
}

export type Restore = {
  onValid?: (data?: NoteType) => void
  onSuccess?: (data?: NoteTypeWithStatus) => void
}

export type Create = {
  onValid?: (data?: NoteType) => void
  onSuccess?: (data?: NoteTypeWithStatus) => void
  user_id?: string
}

export type Edit = {
  onValid?: (data?: NoteType) => void
  onSuccess?: (data?: NoteTypeWithStatus) => void
}

export type EditComment = {
  onValid?: (data?: CommentTypeWithStatus) => void
  onSuccess?: (data?: CommentTypeWithStatus) => void
}

export type Delete = {
  onValid?: (data?: NoteType) => void
  onSuccess?: (data?: NoteTypeWithStatus) => void
}

export type DeleteComment = {
  onValid?: (data?: CommentTypeWithStatus) => void
  onSuccess?: (data?: CommentTypeWithStatus) => void
}

export type Archive = {
  onValid?: (data?: CommentType) => void
  onSuccess?: (data?: CommentType) => void
}

export type ArchiveSuccess = Archive & {
  data?: CommentType
}

export default function useNoteActions(noteParams: NoteActions) {
  const dispatch = useDispatch()
  const { auth: user } = useSelector(authSelector)

  const [, onDeleteNoteMutation] = useDeleteNode({
    node: 'note',
    isBase: false,
  })
  const [, onDeleteCommentMutation] = useDeleteNode({
    node: 'note/comment',
    isBase: false,
  })
  const [, onCreateNoteMutation] = useCreateNode({
    node: 'note',
    isBase: false,
  })

  const [, onUpdateNoteMutation] = useMutation({
    url: 'note',
    method: 'PUT',
  })

  const [, onUpdateCommentMutation] = useMutation({
    url: 'note/comment',
    method: 'PUT',
    isBase: false,
  })

  const [, onFlagParticipantMutation] = useMutation({
    url: 'note/flag',
    method: 'POST',
  })

  function onMemberFlag(params?: MemberFlagParams) {
    dispatch(
      showDialog({
        component: FlagActionConfirmationDialog,
        props: {
          dialogClassName: 'note-dialog',
          continueLabel: 'Flag Member',
          isFlag: true,
          initialFields: {
            ...getEmptyConfirmation(),
            type: 'stop',
          },
          async onValid(data: ConfirmationType) {
            if (params?.onValid) return params?.onValid(data)

            onFlagParticipantMutation({
              data: {
                user_id: noteParams.user_id || params?.user_id || user?.id,
                message: data?.message,
                type: data?.type,
                action: 'Flag',
              },
              message: `Participant flagged successfully`,
              onSuccess: params?.onSuccess ? params?.onSuccess : () => {},
            })
          },
        },
      }),
    )
  }

  function onMemberUnFlag(params?: MemberUnFlagParams) {
    dispatch(
      showDialog({
        component: UnflagDialog,
        props: {
          label: 'Please write a comment or reason for unflagging this Member',
          title: 'Unflag This Member?',
          continueLabel: 'Flag Member',
          dialogClassName: 'note-dialog note-dialog--no-footer',
          hasCancel: false,
          hasContinue: false,
          initialFields: {
            ...getEmptyConfirmation(),
          },
          async onValid(data: ConfirmationType) {
            if (params?.onValid) return params?.onValid(data)

            onFlagParticipantMutation({
              data: {
                user_id: noteParams.user_id || params?.user_id || user?.id,
                message: data?.message,
                action: 'Unflag',
                type: params?.type == 'warn' ? 'warn' : 'stop',
              },
              message: `Participant successfully unflagged`,
              onSuccess: params?.onSuccess ? params?.onSuccess : () => {},
            })
          },
        },
      }),
    )
  }

  function onPreview(params?: Preview) {
    return (data: NoteType) => {
      dispatch(hideDialog())
      dispatch(
        showDialog({
          component: PreviewNoteDialog,
          props: {
            note_id: params?.note_id,
            allowRoleVisibility: noteParams?.hasAdminPermission,
            dialogClassName: 'note-dialog note-dialog--no-footer',
            hasCancel: false,
            hasContinue: false,
            onEdit: onEdit(),
            onDelete: onDelete(),
            onRestore: onRestore(),
            onArchive: onArchive(),
            onDeleteComment: onDeleteComment(),
            onEditComment: onEditComment(),
            onPreviewAttachment: onPreviewAttachment(params),
            refetch: noteParams?.refetch,
            initialFields: {
              ...getEmptyNote(),
              ...data,
            },
            async onValid(data: NoteType) {
              if (params?.onValid) params?.onValid(data)
            },
            onCancel() {
              if (params?.onSuccess) params?.onSuccess()
            },
          },
        }),
      )
    }
  }

  function onPreviewNote(params?: Preview) {
    return (data: NoteType): /*React.MouseEventHandler<HTMLDivElement>*/ any => (event?: React.MouseEvent): void => {
      onPreview(params)(data)
    }
  }

  function onPreviewSystemNote(params?: Preview) {
    return (data: NoteType): /*React.MouseEventHandler<HTMLDivElement>*/ any => (event?: React.MouseEvent): void => {
      dispatch(
        showDialog({
          component: BasicDialog,
          props: {
            title: 'Flagged Member',
            description: `${upperFirst(
              params?.participant?.first_name,
            )} did not pass the Screening test. An Admin will follow up with this member.`,
            dialogClassName: 'note-dialog note-dialog--no-footer',
            hasCancel: false,
            hasContinue: false,
            commentButton: true,
            async onValid() {
              dispatch(hideDialog())
              onPreview(params)(data)
            },
          },
        }),
      )
    }
  }

  function onPreviewAttachment(params?: Preview) {
    return (data: NoteType): React.MouseEventHandler<HTMLDivElement> => (event?: React.MouseEvent): void => {
      if (event) event?.stopPropagation()

      dispatch(
        showDialog({
          component: PreviewAttachment,
          props: {
            dialogClassName: 'note-dialog note-dialog--no-footer',
            hasCancel: false,
            hasContinue: false,
            initialFields: data,
            async onValid() {
              dispatch(hideDialog())
              onPreview(params)(data)
            },
          },
        }),
      )
    }
  }

  function onArchiveSuccess(params?: ArchiveSuccess) {
    return (): void => {
      dispatch(hideDialog())
      dispatch(
        showDialog({
          component: BasicDialog,
          props: {
            title: 'Note Archived',
            description: 'You can restore a note anytime by going to Archived Notes.',
            dialogClassName: 'note-dialog note-dialog--no-footer',
            hasCancel: false,
            hasContinue: false,
            closeButton: true,
          },
        }),
      )
    }
  }

  function onArchive(params?: Archive) {
    return (note: NoteType): void => {
      dispatch(
        showDialog({
          component: ActionConfirmationDialog,
          props: {
            label: 'Reason for archiving note',
            title: 'Archive This Note?',
            dialogClassName: 'note-dialog',
            continueLabel: 'Archive Note',
            initialFields: {
              ...getEmptyComment(),
              note_id: note?.id,
              type: 'Archive Note',
            },
            async onValid(data: CommentType) {
              if (params?.onValid) return params?.onValid(data)

              onUpdateNoteMutation({
                data: {
                  ...data,
                  comment_id: data?.id,
                  id: data?.note_id,
                  action: 'Archive',
                  status: 'Archive',
                },
                onSuccess() {
                  if (params?.onSuccess) params?.onSuccess(data)

                  dispatch(hideDialog())
                  noteParams?.refetch?.()
                  onArchiveSuccess({ ...params, data })()
                },
              })
            },
          },
        }),
      )
    }
  }

  function onRestore(params?: Restore) {
    return (data: NoteType): void => {
      dispatch(
        showDialog({
          component: ConfirmationDialog,
          props: {
            title: 'Restore Note',
            continueLabel: 'Restore Note',
            description: 'Are you sure you want to restore this note? You will not be able to undo this action.',
            dialogClassName: 'note-dialog',
            async onValid() {
              if (params?.onValid) return params?.onValid(data)

              onUpdateNoteMutation({
                data: {
                  ...pick(data, ['id']),
                  action: 'Restore',
                },
                message: `Note successfully restored`,
                onSuccess: params?.onSuccess
                  ? params?.onSuccess
                  : () => {
                      dispatch(hideDialog())
                      noteParams?.refetch?.()
                    },
              })
            },
          },
        }),
      )
    }
  }

  function onDelete(params?: Delete) {
    return (data: NoteType): void => {
      dispatch(
        showDialog({
          component: ConfirmationDialog,
          props: {
            title: 'Delete Note',
            continueLabel: 'Delete Note',
            description: 'Are you sure you want to delete this note? You will not be able to undo this action.',
            dialogClassName: 'note-dialog',
            async onValid() {
              if (params?.onValid) return params?.onValid(data)

              onDeleteNoteMutation({
                data: { id: data?.id },
                onSuccess: params?.onSuccess
                  ? params?.onSuccess
                  : () => {
                      dispatch(hideDialog())
                      noteParams?.refetch?.()
                    },
              })
            },
          },
        }),
      )
    }
  }

  function onDeleteComment(params?: DeleteComment) {
    return (data: CommentTypeWithStatus): void => {
      dispatch(
        showDialog({
          component: ActionConfirmationDialog,
          props: {
            label: 'Reason for deleting comment',
            title: 'Delete This Comment?',
            dialogClassName: 'note-dialog',
            continueLabel: 'Delete Comment',
            description: 'Are you sure you want to delete this comment? You will not be able to undo this action.',
            initialFields: {
              ...getEmptyConfirmation(),
            },
            async onValid(confirmation: ConfirmationType) {
              onDeleteCommentMutation({
                data: {
                  ...pick(data, ['id', 'note_id']),
                  confirmation,
                },
                message: `Comment successfully deleted`,
                onSuccess: params?.onSuccess
                  ? params?.onSuccess
                  : () => {
                      // dispatch(hideDialog())
                      // noteParams?.refetch?.()
                    },
              })
            },
          },
        }),
      )
    }
  }

  function onEdit(params?: Edit) {
    return (data: NoteType): void => {
      dispatch(
        showDialog({
          component: CreateNoteDialog,
          props: {
            isEdit: true,
            allowRoleVisibility: noteParams?.hasAdminPermission,
            title: 'Edit Note',
            dialogClassName: 'note-dialog',
            continueLabel: 'Save Note',
            initialFields: {
              ...getEmptyNote(),
              ...data,
            },
            async onValid(data: NoteType) {
              if (params?.onValid) return params?.onValid(data)

              onUpdateNoteMutation({
                data: {
                  ...pick(data, ['id', 'message']),
                  action: 'Edit',
                },
                message: `Note successfully updated`,
                onSuccess: params?.onSuccess ? params?.onSuccess : () => {},
              })
            },
          },
        }),
      )
    }
  }

  function onCreate(params?: Create) {
    return (): void => {
      dispatch(
        showDialog({
          component: CreateNoteDialog,
          props: {
            isEdit: false,
            allowRoleVisibility: noteParams?.hasAdminPermission,
            title: 'Create A Note',
            continueLabel: 'Create Note',
            dialogClassName: 'note-dialog',
            initialFields: getEmptyNote(),
            async onValid(data: NoteType) {
              if (params?.onValid) return params?.onValid(data)

              onCreateNoteMutation({
                data: { ...data, user_id: noteParams.user_id || params?.user_id || user?.id },
                onSuccess: params?.onSuccess ? params?.onSuccess : () => {},
              })
            },
          },
        }),
      )
    }
  }

  function onEditComment(params?: EditComment) {
    return (data: CommentTypeWithStatus): void => {
      dispatch(
        showDialog({
          component: CreateNoteDialog,
          props: {
            isEdit: true,
            isComment: true,
            allowRoleVisibility: noteParams?.hasAdminPermission,
            title: 'Edit Comment',
            dialogClassName: 'note-dialog',
            continueLabel: 'Save Comment',
            initialFields: {
              ...getEmptyComment(),
              ...data,
            },
            async onValid(data: CommentTypeWithStatus) {
              onUpdateCommentMutation({
                data: {
                  ...pick(data, ['message', 'note_id']),
                  comment_id: data?.id,
                  action: 'Edit',
                },
                message: `Comment successfully updated`,
                onSuccess: params?.onSuccess ? params?.onSuccess : () => {},
              })
            },
          },
        }),
      )
    }
  }

  return {
    onCreate,
    onEdit,
    onDelete,
    onDeleteComment,
    onRestore,
    onArchive,
    onArchiveSuccess,
    onPreviewAttachment,
    onPreviewSystemNote,
    onPreviewNote,
    onPreview,
    onMemberUnFlag,
    onMemberFlag,
  }
}
